import * as React from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

import UnselectedIcon from '@components/basics/icons/unselected';
import CreditCardIcon from '@components/basics/icons/credit-card';
import VisaLogo from '@components/basics/icons/credit-card-brands/visa';
import AmexLogo from '@components/basics/icons/credit-card-brands/amex';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import MastercardLogo from '@components/basics/icons/credit-card-brands/mastercard';

import { CardBrands } from '@services/payment/types/card-brands';
import { SimplePaymentMethod } from '@services/payment/types/simple-payment-method';

interface NewMyCardCtrlProps {
  className?: string;
  creditCard: SimplePaymentMethod;
  active?: boolean;
  onSelection: (creditCard?: SimplePaymentMethod) => void;
  isMobile: boolean;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

export const NewMyCardCtrl: React.FC<NewMyCardCtrlProps> = ({
  className,
  creditCard,
  active,
  onSelection,
  isMobile,
}) => {
  const handleSelection = () => {
    return onSelection ? onSelection(active ? undefined : creditCard) : null;
  };

  React.useEffect(() => {
    handleSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCreditCardDecoration = (brand: CardBrands) => {
    switch (brand) {
      case 'visa':
        return <VisaLogo className="credit-card-brand" />;
      case 'amex':
        return <AmexLogo className="credit-card-brand" />;
      case 'mastercard':
        return <MastercardLogo className="credit-card-brand" />;
      case 'unknown':
        return <></>;

      default:
        return (
          <Typography
            capitalize
            type="smaller"
            className="credit-card-brand-text"
            weight="700"
          >
            {brand}
          </Typography>
        );
    }
  };

  return (
    <CardWrapper
      className={`${className} ${active ? 'selected' : ''}`}
      onClick={handleSelection}
    >
      <Flex
        justifyContent="flex-start"
        alignItems="center"
        gap={isMobile ? 8 : 20}
      >
        <CreditCardIcon className="credit-card-icon" />
        <Info>
          <Typography type="smaller" className="label">
            <Translate id="ARTIST_PROFILE.SUBSCRIPTION.BILLING.CREDIT_CARD.LABEL" />
          </Typography>
          <Typography className="number">
            **** **** **** {creditCard.last4}
          </Typography>
        </Info>
      </Flex>
      <Flex justifyContent="flex-end" alignItems="center" gap={10}>
        {getCreditCardDecoration(creditCard.brand)}
        {active ? (
          <CompletedIcon className="icon" />
        ) : (
          <UnselectedIcon className="icon" />
        )}
      </Flex>
    </CardWrapper>
  );
};

export default connect(mapStateToProps)(NewMyCardCtrl);

const CardWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  min-height: 48px;
  border-radius: 4px;
  position: relative;
  padding: 0 20px;

  .credit-card-brand {
    max-width: 42px;
  }

  &.selected {
    background-color: ${({ theme }) => theme.colors.primary};

    .credit-card-icon,
    .credit-card-brand {
      path {
        fill: ${({ theme }) => theme.colors.differentBackground};
      }
    }

    .credit-card-brand-text,
    .label,
    .number {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }

  .icon {
    width: 24px;
    height: 24px;
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    min-height: 60px;
  }
`;

const Info = styled.div`
  display: grid;
  grid-gap: 2px;

  .label {
    font-weight: 400;
  }
  .number {
    color: ${({ theme }) => theme.colors.tertiary};
    font-weight: 700;
  }
`;
